import { useCallback, useRef, useState } from "react";

import { getApiErrorMessage } from "@/api";

export type FormRegister = (
  field: string,
  props?: {
    ref: React.RefObject<HTMLInputElement | null>;
    validate?: () => string | null;
    getValue: () => string;
  },
) => void;

type FormResult = {
  formProps: React.ComponentPropsWithoutRef<"form">;
  register: FormRegister;
  serverError: string | null;
  clearServerError: () => void;
  status: "idle" | "submitting" | "success" | "error";
};

export type FormSubmitHandler<T extends string> = (
  fields: Record<T, string>,
  e: React.FormEvent<HTMLFormElement>,
) => Promise<void>;

export function useForm<T extends string>(submit: FormSubmitHandler<T>): FormResult {
  const [serverError, setServerError] = useState<string | null>(null);
  const [status, setStatus] = useState<FormResult["status"]>("idle");

  const fieldRef = useRef(
    new Map<
      string,
      {
        ref: React.RefObject<HTMLInputElement | null>;
        validate: () => string | null;
        getValue: () => string;
      }
    >(),
  );

  const register = useCallback<FormRegister>((field, opts) => {
    if (opts) {
      const { ref, validate, getValue } = opts;
      fieldRef.current.set(field, {
        ref,
        validate: validate ?? (() => null),
        getValue,
      });
    } else {
      fieldRef.current.delete(field);
    }
  }, []);

  const clearServerError = useCallback(() => {
    setServerError(null);
  }, []);

  return {
    register,
    serverError,
    clearServerError,
    status,
    formProps: {
      onSubmit: async (e) => {
        const values: Record<string, string> = {};
        let errorFound = false;
        for (const [field, { ref, validate, getValue }] of fieldRef.current.entries()) {
          if (validate() != null && !errorFound) {
            errorFound = true;
            e.preventDefault();
            ref.current?.focus();
          }
          values[field] = getValue();
        }

        if (errorFound) return;

        setServerError(null);
        setStatus("submitting");

        try {
          await submit(values, e);
          setStatus("success");
        } catch (err) {
          setStatus("error");
          setServerError(getApiErrorMessage(err));
        }
      },
    },
  };
}
